import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { toast } from "sonner";
import { ArrowLeft, Percent, Star, Wallet, User, Pencil, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CarteirinhaButton } from "@/components/CarteirinhaDialog";
import { OverallStickerButton } from "@/components/OverallStickerDialog";
import { EvaluationCompare } from "@/components/EvaluationCompare";
import { EvaluationDialog } from "@/components/EvaluationDialog";
import { useConfirm } from "@/components/ConfirmDialog";

export default function StudentDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [att, setAtt] = useState(null);
  const [evals, setEvals] = useState([]);
  const [pays, setPays] = useState([]);
  const [evalOpen, setEvalOpen] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const confirm = useConfirm();

  const reloadEvals = async () => {
    try {
      const { data } = await api.get(`/evaluations/student/${id}`);
      setEvals(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [sRes, aRes, eRes, pRes] = await Promise.all([
          api.get(`/students/${id}`),
          api.get(`/attendance/student/${id}`),
          api.get(`/evaluations/student/${id}`),
          api.get(`/payments/student/${id}`),
        ]);
        setS(sRes.data); setAtt(aRes.data); setEvals(eRes.data); setPays(pRes.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [id]);

  const delEval = async (eid) => {
    const ok = await confirm({ title: "Excluir avaliação?", confirmText: "Excluir", destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/evaluations/${eid}`);
      toast.success("Excluída");
      setEvals(evals.filter(e => e.id !== eid));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!s) return <div className="text-muted-foreground">Carregando...</div>;

  const allScores = evals.flatMap(e => Object.values(e.scores || {}));
  const avgEval = allScores.length ? (allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(1) : "—";

  return (
    <div data-testid="student-detail-page">
      <div className="flex items-center justify-between mb-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/alunos"><ArrowLeft className="size-4 mr-2" /> Voltar</Link>
        </Button>
        <div className="flex flex-wrap gap-2">
          <OverallStickerButton student={s} evaluations={evals} />
          <CarteirinhaButton student={s} stats={{ attendance: att?.percentage ?? 0, evals: evals.length, avgScore: avgEval }} />
        </div>
      </div>

      <div className="vv-card p-6 mb-8 flex items-center gap-5 flex-wrap">
        <div className="size-28 rounded-2xl overflow-hidden border-4 border-[hsl(var(--vv-lime))/0.5] shadow-lg">
          {s.photo_url ? (
            <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" data-testid="student-photo" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <User className="size-14 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--vv-lime))]">{s.category}</div>
          <div className="leading-tight">
            <h1 className="text-3xl sm:text-4xl font-black break-words">{s.first_name || (s.name?.split(" ")[0] || s.name)}</h1>
            {(s.last_name || (s.name?.split(" ").slice(1).join(" "))) && (
              <div className="text-base sm:text-lg font-bold text-muted-foreground mt-0.5 break-words">
                {s.last_name || s.name?.split(" ").slice(1).join(" ")}
              </div>
            )}
          </div>
          <div className="text-muted-foreground mt-1 text-sm break-all">{s.email} • {s.position || "Sem posição"} • {s.status}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Presença" value={`${att?.percentage ?? 0}%`} icon={Percent} color="lime" />
        <StatCard label="Avaliações" value={evals.length} icon={Star} color="yellow" />
        <StatCard label="Nota média" value={avgEval} icon={Star} color="blue" />
        <StatCard label="Pagamentos" value={pays.length} icon={Wallet} color="lime" />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Dados</TabsTrigger>
          <TabsTrigger value="evaluations" data-testid="tab-evaluations">Avaliações</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">Comparar</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Presença</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">Financeiro</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="vv-card p-5">
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Nascimento" value={s.birth_date} />
              <Info label="CPF" value={s.cpf || "—"} />
              <Info label="Categoria" value={s.category} />
              <Info label="Posição" value={s.position || "—"} />
              <Info label="Telefone" value={s.phone || "—"} />
              <Info label="Responsável" value={s.guardian || "—"} />
              <Info label="Tel. responsável" value={s.guardian_phone || "—"} />
              <Info label="Status" value={s.status} />
            </dl>
          </div>
        </TabsContent>

        <TabsContent value="evaluations">
          <div className="flex justify-end mb-3">
            <Button
              onClick={() => { setEditingEval(null); setEvalOpen(true); }}
              className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider"
              data-testid="detail-new-eval-button"
            >
              <Plus className="size-4 mr-2" /> Nova avaliação
            </Button>
          </div>
          {evals.length === 0 ? (
            <div className="vv-card p-6 text-muted-foreground text-sm">Nenhuma avaliação registrada.</div>
          ) : (
            <div className="space-y-3">
              {[...evals].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => (
                <div key={e.id} className="vv-card p-4" data-testid={`detail-eval-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-black">{e.date}</div>
                      {e.notes && <div className="text-xs text-muted-foreground">{e.notes}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => { setEditingEval(e); setEvalOpen(true); }}
                        data-testid={`detail-edit-eval-${e.id}`}
                        title="Editar"
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="text-destructive"
                        onClick={() => delEval(e.id)}
                        data-testid={`detail-delete-eval-${e.id}`}
                        title="Excluir"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                    {Object.entries(e.scores || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between bg-muted/40 rounded px-2 py-1 text-sm">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-black text-[hsl(var(--vv-lime))]">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="compare">
          <EvaluationCompare evaluations={evals} />
        </TabsContent>

        <TabsContent value="attendance">
          <div className="vv-card p-5">
            <div className="text-sm space-y-1.5 max-h-96 overflow-auto">
              {att?.history?.length ? att.history.map((h, i) => (
                <div key={i} className="flex justify-between border-b border-border/60 py-1.5">
                  <span>{h.date}</span>
                  <span className={h.present ? "text-[hsl(var(--vv-lime))]" : "text-destructive"}>
                    {h.present ? "Presente" : "Faltou"}
                  </span>
                </div>
              )) : <div className="text-muted-foreground">Sem registros.</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="finance">
          <div className="vv-card p-5">
            <div className="text-sm space-y-1.5">
              {pays.length ? pays.map((p) => (
                <div key={p.id} className="flex justify-between border-b border-border/60 py-1.5">
                  <span>{p.month}{p.amount ? ` • R$ ${Number(p.amount).toFixed(2)}` : ""}</span>
                  <span className={`vv-chip ${p.status === "pago" ? "vv-chip-lime" : "vv-chip-yellow"}`}>{p.status}</span>
                </div>
              )) : <div className="text-muted-foreground">Sem registros.</div>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <EvaluationDialog
        open={evalOpen}
        onOpenChange={setEvalOpen}
        studentId={id}
        evaluation={editingEval}
        onSaved={reloadEvals}
      />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between border-b border-border/60 pb-1.5">
      <dt className="text-muted-foreground text-xs uppercase tracking-wider">{label}</dt>
      <dd className="font-semibold">{value}</dd>
    </div>
  );
}
